import { Injectable } from "@angular/core";
import { HttpService } from "app/js/src/infrastructure/http/http";
import { UserService } from "app/js/src/domain/user-service";
import { Font } from "app/js/src/domain/font";

export interface ClippingStudioFontOption {
  id: number;
  name: string;
}

export interface EnsureFontResult {
  /** CSS font-family to use for preview (loaded face or sans-serif). */
  cssFamily: string;
  /** True only when the font file was loaded successfully. */
  loaded: boolean;
}

/**
 * Loads tenant fonts for Clipping Studio preview (Text + Subtitles).
 * Registers faces the same way as videoContainer (@font-face via a style tag).
 * Successes are cached in memory for the session; failures are not cached.
 */
@Injectable()
export class ClippingStudioFontService {
  private static readonly FALLBACK_FAMILY = "sans-serif";

  /** fontId → registered CSS family name after a successful load. */
  private readonly successById = new Map<number, string>();
  /** In-flight loads coalesced per fontId (not retained after settle). */
  private readonly inflightById = new Map<number, Promise<EnsureFontResult>>();

  constructor(
    private readonly httpService: HttpService,
    private readonly userService: UserService,
  ) {}

  getAvailableFonts(): ClippingStudioFontOption[] {
    return this.getTenantFonts()
      .filter((f) => !!f?.name && f.name.trim().length > 0)
      .map((f) => ({ id: f.id, name: f.name.trim() }));
  }

  getAvailableFontNames(): string[] {
    return this.getAvailableFonts().map((f) => f.name);
  }

  /**
   * Sync lookup for canvas/DOM: loaded CSS family, or sans-serif if not yet
   * successfully loaded.
   */
  getCssFamily(fontName: string | null | undefined): string {
    const id = this.findFontId(fontName);
    if (id == null) {
      return ClippingStudioFontService.FALLBACK_FAMILY;
    }
    return (
      this.successById.get(id) ?? ClippingStudioFontService.FALLBACK_FAMILY
    );
  }

  /**
   * Ensures the font file for the given catalog name is available to the browser.
   * On failure returns sans-serif and does not cache the error (retry on next call).
   */
  async ensureFontLoaded(
    fontName: string | null | undefined,
  ): Promise<EnsureFontResult> {
    const name = (fontName || "").trim();
    const id = this.findFontId(name);
    if (id == null) {
      console.warn(
        `[ClippingStudioFont] Font file could not be loaded. name=${name || "(empty)"} (not in tenant catalog)`,
      );
      return {
        cssFamily: ClippingStudioFontService.FALLBACK_FAMILY,
        loaded: false,
      };
    }

    const cached = this.successById.get(id);
    if (cached) {
      return { cssFamily: cached, loaded: true };
    }

    let inflight = this.inflightById.get(id);
    if (!inflight) {
      inflight = this.loadFont(id, name);
      this.inflightById.set(id, inflight);
      try {
        return await inflight;
      } finally {
        this.inflightById.delete(id);
      }
    }
    return inflight;
  }

  private getTenantFonts(): Font[] {
    return this.userService.getCurrentUser()?.fonts ?? [];
  }

  private findFontId(fontName: string | null | undefined): number | null {
    const name = (fontName || "").trim().toLowerCase();
    if (!name) {
      return null;
    }
    const match = this.getTenantFonts().find(
      (f) => (f.name || "").trim().toLowerCase() === name,
    );
    return match != null ? match.id : null;
  }

  /** Unique CSS family per font id (same idea as videoContainer's subtitles-font-${id}). */
  private cssFamilyForId(id: number): string {
    return `cs-font-${id}`;
  }

  private async loadFont(id: number, name: string): Promise<EnsureFontResult> {
    const cssFamily = this.cssFamilyForId(id);
    try {
      const url = await this.httpService.get(`Font/${id}/GetFontUrl`);
      if (!url || typeof url !== "string") {
        throw new Error("empty font url");
      }

      const style = document.createElement("style");
      style.innerHTML = `@font-face{ font-family: ${cssFamily}; src:url('${url}'); }`;
      document.head.appendChild(style);

      // Canvas needs the face ready; videoContainer can skip this for DOM CSS.
      if (document.fonts?.load) {
        await document.fonts.load(`16px ${cssFamily}`);
      }

      this.successById.set(id, cssFamily);
      return { cssFamily, loaded: true };
    } catch (err) {
      console.warn(
        `[ClippingStudioFont] Font file could not be loaded. fontId=${id} name=${name}`,
        err,
      );
      return {
        cssFamily: ClippingStudioFontService.FALLBACK_FAMILY,
        loaded: false,
      };
    }
  }
}
